import { useCallback, useState } from 'react';
import { ArticlesView, GrowthDashboardTopBarActions } from '@elva/ui';
import { useNavigate } from 'react-router-dom';
import { AsyncBoundary } from '@/components/feedback';
import { usePageTopBar } from '@/app/layouts/pageTopBar';
import { viewProps } from '@/data/viewProps';
import { useArticleMutations, useArticles } from '../hooks/useArticles';

export function ArticlesPage() {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [creatingArticleId, setCreatingArticleId] = useState('');

  usePageTopBar({
    actions: <GrowthDashboardTopBarActions />,
  });

  const { data, isPending, isError, error, refetch, isRefetching, isFetching } = useArticles({ page });
  const { regenerate } = useArticleMutations();

  const handleCreateArticle = useCallback(
    (id: string) => {
      setCreatingArticleId(id);
      regenerate.mutate(id, {
        onSuccess: () => {
          navigate(`/articles/${id}`);
        },
        onSettled: () => {
          setCreatingArticleId('');
        },
      });
    },
    [navigate, regenerate],
  );

  return (
    <AsyncBoundary
      loading={isPending}
      error={data === undefined && isError ? error : undefined}
      onRetry={refetch}
      retrying={isRefetching}
      isEmpty={
        data !== undefined &&
        data.newArticles.length === 0 &&
        data.existingArticles.length === 0 &&
        page === 1
      }
    >
      <ArticlesView
        {...viewProps({
          newArticles: data?.newArticles ?? [],
          existingArticles: data?.existingArticles ?? [],
          stats: data?.stats ?? [],
          calendar: data?.calendar ?? [],
          pagination: data?.pagination,
          loading: isFetching,
          initialTab: (
            (data?.newArticles.length ?? 0) === 0 && (data?.existingArticles.length ?? 0) > 0
              ? ('existing' as const)
              : ('new' as const)
          ),
          onPageChange: setPage,
          onViewArticle: (id: string) => navigate(`/articles/${id}`),
          onCreateArticle: handleCreateArticle,
          creatingArticleId: creatingArticleId || undefined,
          onNewArticle: () => navigate('/articles/new'),
        })}
      />
    </AsyncBoundary>
  );
}
